// Engagement letter template + scope schedules, rendered in /admin → Engagement Letter.
// Template only - to be reviewed by a lawyer before client use.

export const ENGAGEMENT_META = {
  title: "Clarus Partners - Engagement Letter & Scope Templates",
  subtitle: "Master engagement letter with per-tier scope schedules · fill [bracketed] fields per client",
  updated: "Template - have it reviewed by your lawyer before first use",
};

export const ENGAGEMENT_LETTER = [
  {
    id: "parties",
    title: "1. Parties & engagement",
    blocks: [
      { type: "p", text: "This letter confirms the engagement of Clarus Partners (ABN 49 308 724 557) ('we', 'us') by [Client legal name] (ABN [client ABN]) ('you') to provide the bookkeeping and finance support services described in the Scope Schedule(s) attached, from [start date]." },
      { type: "p", text: "Services are provided remotely by Nima Riazati, CPA. All work is performed in your Xero file unless otherwise agreed in writing." },
    ],
  },
  {
    id: "scope",
    title: "2. Scope of services",
    blocks: [
      { type: "p", text: "We will provide the services listed in the Scope Schedule for your selected plan (Essentials, Growth, Control or Partner), plus any one-off project scope agreed in writing. Anything not listed is out of scope and will be quoted separately before work starts." },
      { type: "list", items: [
        "Fair-use limits (transaction volumes, employee counts, bank accounts, documents) are set out in your Scope Schedule. Usage is reviewed quarterly from Xero reports.",
        "If you exceed a limit for two consecutive months, we will propose an upgraded plan or a fixed-price variation - we will never absorb ongoing extra work silently or bill it without agreement.",
        "Ad-hoc work outside scope is quoted fixed-price, or at $180/hour + GST with your prior approval. [NIMA TO CONFIRM rate]",
      ]},
    ],
  },
  {
    id: "support",
    title: "3. Support & response times",
    blocks: [
      { type: "list", items: [
        "You have unlimited support by email and phone on matters within the scope of your plan, with a same-business-day response. Partner plan clients receive first-in-queue priority.",
        "Casual phone/email queries are time-boxed to about 15 minutes. Anything that needs real work (analysis, changes, new reports) is quoted as a variation - we will tell you before the meter runs.",
        "Scheduled check-in calls are as per your plan (none on Essentials, monthly on Growth, fortnightly on Control, up to two meetings/month on Partner).",
      ]},
    ],
  },
  {
    id: "boundary",
    title: "4. What we don't do - tax & BAS boundary",
    blocks: [
      { type: "p", text: "Clarus Partners is not a registered tax agent or BAS agent and does not provide tax agent services or BAS services. Our work is prepared for review and lodgement by your registered tax or BAS agent. GST coding is applied in accordance with your registered agent's instructions, and your registered agent reviews the figures before relying on them for any lodgement. You should not rely on our work product, without that review, to satisfy obligations or claim entitlements under any taxation law." },
      { type: "p", text: "We never lodge BAS, tax returns or other statutory forms on your behalf. Lodgements remain with your registered agent." },
    ],
  },
  {
    id: "responsibilities",
    title: "5. Your responsibilities",
    blocks: [
      { type: "list", items: [
        "Provide and maintain Xero access, bank feeds and the receipt-capture app, and keep subscription fees for your own software current.",
        "Forward source documents (receipts, bills, statements) promptly - the month-end close clock starts when documents are complete.",
        "Tell us about anything unusual: new entities, loans, asset purchases, staff changes, ATO correspondence.",
        "Review the reports we send and raise questions within 5 business days.",
      ]},
    ],
  },
  {
    id: "fees",
    title: "6. Fees, billing & price review",
    blocks: [
      { type: "list", items: [
        "Fixed monthly fee per your plan (Essentials from $1,000, Growth from $2,000, Control from $3,000, Partner from $4,000 per month), plus GST, confirmed in your Scope Schedule.",
        "Billed monthly in advance by direct debit. One-off projects are invoiced on the agreed fixed price, 50% on commencement and 50% on completion unless otherwise stated.",
        "An annual price review applies each July, linked to CPI. We will give 30 days' written notice of any change.",
        "Annual prepay discount of 10% is available after the initial term by agreement.",
      ]},
    ],
  },
  {
    id: "term",
    title: "7. Term & termination",
    blocks: [
      { type: "p", text: "A minimum initial term of 3 months applies from the start date - month one is set-up done properly, and three months shows you the rhythm. After that the engagement rolls monthly and either party may end it with 30 days' written notice. On termination we hand over your file in good order; your Xero data is always yours." },
    ],
  },
  {
    id: "legal",
    title: "8. Confidentiality, privacy & liability",
    blocks: [
      { type: "list", items: [
        "We keep your information confidential and handle personal information per our Privacy Policy and the Australian Privacy Principles.",
        "Our liability is limited to the fees you paid us in the 12 months before the event giving rise to the claim. We hold professional indemnity insurance as required by CPA Australia By-Law 9.8. [NIMA TO CONFIRM policy in place]",
        "We own our working papers; you own your books and records. Reports we produce for you are yours to use within your business and share with your registered agents.",
        "This agreement is governed by the law of [State/Territory - NIMA TO CONFIRM]. It is the entire agreement and may only be varied in writing.",
      ]},
    ],
  },
  {
    id: "schedules",
    title: "Scope Schedules A-D (attach the one that applies)",
    blocks: [
      {
        type: "table",
        headers: ["", "Schedule A - Essentials", "Schedule B - Growth", "Schedule C - Control", "Schedule D - Partner"],
        rows: [
          ["Monthly fee", "From $1,000 + GST", "From $2,000 + GST", "From $3,000 + GST", "From $4,000 + GST"],
          ["Included", "Transaction coding, monthly bank & credit card reconciliations, receipt chasing, one-page monthly P&L summary", "Schedule A + month-end close, management report pack with commentary, payroll & super, AP/AR processing", "Schedule B + 13-week cashflow forecast, budgets vs actuals, job/project profitability, integrations & automation", "Schedule C + KPI dashboard, board/investor pack, pricing & scenario modelling, strategic advice"],
          ["Fair-use limits", "≤150 transactions/mo · 1 entity · 2 bank/credit accounts · no payroll", "≤300 transactions/mo · ≤20 AP/AR documents · payroll ≤5 employees (+$15/employee above)", "≤600 transactions/mo · ≤40 AP/AR documents · payroll ≤15 employees · 1 integration", "≤600 transactions/mo · payroll ≤15 employees · deliverables within ~2 days/month of senior time"],
          ["Check-ins", "None scheduled - email & phone within scope", "Monthly 30-minute call", "Fortnightly 30-minute call", "Up to 2 meetings/month"],
          ["Response", "Same business day", "Same business day", "Same business day", "Same business day, first in queue"],
        ],
      },
      { type: "callout", text: "One-off project scopes (Health Check, Clean-Up Sprint, Launch Pack, payroll audit, integrations, EOFY pack) attach as Schedule E with the fixed price and timeframe from your quote." },
    ],
  },
];

const PLAN_SCHEDULE_COL = { Essentials: 1, Growth: 2, Control: 3, Partner: 4 };
const PLAN_FROM_PRICE = { Essentials: "1000", Growth: "2000", Control: "3000", Partner: "4000" };

export { PLAN_FROM_PRICE };

export function buildProposalDoc({ clientName, clientAbn, planName, fee, startDate }) {
  const replace = (text) =>
    text
      .replaceAll("[Client legal name]", clientName || "[Client legal name]")
      .replaceAll("[client ABN]", clientAbn || "[client ABN]")
      .replaceAll("[start date]", startDate || "[start date]");

  const col = PLAN_SCHEDULE_COL[planName] ?? 1;
  const feeNum = parseInt(String(fee).replace(/[^0-9]/g, ""), 10);
  const feeText = Number.isFinite(feeNum) ? `$${feeNum.toLocaleString("en-AU")}/month + GST` : "fee to confirm";

  const sections = ENGAGEMENT_LETTER.map((section) => {
    if (section.id !== "schedules") {
      return {
        ...section,
        title: replace(section.title),
        blocks: section.blocks.map((b) => ({
          ...b,
          text: b.text ? replace(b.text) : undefined,
          items: b.items ? b.items.map(replace) : undefined,
        })),
      };
    }
    const src = section.blocks.find((b) => b.type === "table");
    const callout = section.blocks.find((b) => b.type === "callout");
    const table = {
      type: "table",
      headers: ["", `Scope Schedule - ${planName}`],
      rows: src.rows.map((row, i) => [
        row[0],
        i === 0 ? `${feeText} (agreed)` : row[col],
      ]),
    };
    return { ...section, title: `Scope Schedule - ${planName} (attached)`, blocks: [table, callout] };
  });

  return {
    meta: {
      title: `Engagement letter - ${clientName || "Draft client"}`,
      subtitle: `${planName} plan · ${feeText} · starting ${startDate || "[start date]"}`,
      updated: "Review before sending - this template should be lawyer-approved before first use",
    },
    sections,
  };
}
